import { useMemo, useState } from 'react';
import { ExpandMore } from '@material-ui/icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import { TitleDivider } from '../../../components/titleDivider';
import { TableHead } from '@material-ui/core';
import { observer } from 'mobx-react';
import { makeStyles } from '@material-ui/core';
import { platformTokens } from '@roc/feature-app-core';

import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import { formatPercentage } from '@roc/ui/utils';
import { defaultFloorRateBridgeLoans } from '../../../utils/constants';

interface PricingAdjustmentsAccordionProps {
  store: QuoteTypeStore;
}

// tokens are read inside makeStyles (not at module load) because of a circular import with feature-app-core
const useStyles = makeStyles(theme => {
  const { color: platformColor, status, radius } = platformTokens;
  const cellBorder = `1px solid ${platformColor.neutral100}`;
  const bodyCell = {
    border: 'none',
    borderTop: cellBorder,
    backgroundColor: platformColor.surface,
    padding: '10px 16px',
  };
  return {
    container: {
      border: cellBorder,
      borderRadius: radius.lg,
      overflow: 'hidden',
    },
    noBorders: {
      border: 'none',
    },
    headerCell: {
      backgroundColor: platformColor.neutral50,
      color: platformColor.subtle,
      fontWeight: 600,
      border: 'none',
      padding: '10px 16px',
      textAlign: 'center',
    },
    // Base Rate / Adjusted Floor Rate summary rows
    subHeaderCell: {
      ...bodyCell,
      backgroundColor: status.info.bg,
      color: platformColor.heading,
      fontWeight: 'bold',
    },
    valueCell: {
      ...bodyCell,
      borderRight: cellBorder,
      '&:last-child': {
        borderRight: 'none',
      },
    },
    boldFont: {
      fontWeight: 'bold',
    },
    greenFont: {
      ...bodyCell,
      color: theme.palette.success.dark,
      fontWeight: 'bold',
    },
    redFont: {
      ...bodyCell,
      color: theme.palette.error.dark,
      fontWeight: 'bold',
    },
  };
});

export const PricingAdjustmentsAccordion = observer(
  ({ store }: PricingAdjustmentsAccordionProps) => {
    const [expanded, setExpanded] = useState(true);
    const classes = useStyles();
    const { loanEconomicsStore } = store;

    const rows = useMemo(() => {
      const parsedRows =
        loanEconomicsStore.rateAdjustmentsData?.criteriaRateAdjustmentData?.slice() ||
        [];

      parsedRows.sort((a, b) => a.adjustment - b.adjustment);
      return parsedRows;
    }, [loanEconomicsStore.rateAdjustmentsData]);

    return (
      <Accordion
        expanded={expanded}
        onChange={(e, expanded) => setExpanded(expanded)}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore fontSize="large" />}>
          <TitleDivider title="Pricing Adjustments" />
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer className={classes.container}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{ textAlign: 'left', width: '25%' }}
                    className={classes.headerCell}
                  >
                    Criteria
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ width: '50%' }}
                    className={classes.headerCell}
                  >
                    Input
                  </TableCell>
                  <TableCell
                    style={{ width: '25%' }}
                    className={classes.headerCell}
                  >
                    Adjustment
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    className={classes.subHeaderCell}
                  >
                    Base Rate
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    align="center"
                    className={classes.subHeaderCell}
                  />
                  <TableCell
                    component="th"
                    scope="row"
                    align="center"
                    className={classes.subHeaderCell}
                  >
                    {formatPercentage(loanEconomicsStore.rateAdjustmentsData?.baseRate || 10.99)}
                  </TableCell>
                </TableRow>
                {rows.map(row => (
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      className={classes.valueCell}
                    >
                      {row.criteria}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      align="center"
                      className={classes.valueCell}
                    >
                      {row.input}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      align="center"
                      className={
                        row.adjustment > 0
                          ? classes.redFont
                          : row.adjustment < 0
                            ? classes.greenFont
                            : classes.valueCell
                      }
                    >
                      {typeof row.adjustment === 'number'
                        ? formatPercentage(row.adjustment)
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    className={classes.subHeaderCell}
                  >
                    Adjusted Floor Rate
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    align="center"
                    className={classes.subHeaderCell}
                  />
                  <TableCell
                    component="th"
                    scope="row"
                    align="center"
                    className={classes.subHeaderCell}
                  >
                    {formatPercentage(loanEconomicsStore.floorRate || defaultFloorRateBridgeLoans(store.loanSubtype))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  }
);
